import AsyncSelect from 'react-select/async';
import { MultiValue } from 'react-select';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import RecommendConceptsClient from '../../clientAPI/recommendConcepts';
import * as R from 'ramda';
import { useState, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';

type ConceptSelectOptions = {
  value: any | undefined;
  label: string;
};

type ConceptIdsProps = {
  conceptIds: string[] | undefined;
  onChange: (newSelectOpts: MultiValue<{ label: string; value: any }>) => void;
  selectConceptsRef: any;
  validationError: string;
  searchableOnly: boolean | undefined;
};

export default function ConceptSelect(props: ConceptIdsProps) {
  const [selectedConceptOpts, setSelectedConceptOpts] = useState<
    ConceptSelectOptions[]
  >([]);

  const onChange = props.onChange;

  const rankingConcepts = props.conceptIds || [];

  const loaderData = useLoaderData() as { access_token: string };

  const revalidator = useRevalidator();

  const searchableOnly = props.searchableOnly ?? true;

  const handleSelectChange = (
    newSelectOpts: MultiValue<{ label: string; value: any }>,
  ) => {
    const selectedConceptOpts = R.uniqBy(
      (t) => t.value.id,
      newSelectOpts.map((opt) => ({
        value: opt.value,
        label: opt.label,
      })),
    );
    setSelectedConceptOpts(selectedConceptOpts);
    onChange(newSelectOpts);
  };

  const asyncOptions = useDebouncedCallback((inputValue, callback) => {
    RecommendConceptsClient.searchConcepts(inputValue, searchableOnly)
      .then((res) => {
        const opts = res.objects.map((c) => {
          const label =
            c.name === c.alternateName
              ? `${c.name} ${c.source.system}`
              : `${c.name}(${c.alternateName}) ${c.source.system}`;
          return {
            value: {
              id: c.id,
              name: c.name,
            },
            label,
          };
        });
        callback(opts);
      })
      .catch((err: keyable) => {
        console.log(err);
        err.status === 401 && revalidator.revalidate();
        callback([]);
      });
  }, 1000);

  useEffect(() => {
    const conceptQueries = (rankingConcepts as unknown as string[]).map((c) =>
      RecommendConceptsClient.getConceptById(c).catch((err) => {
        console.log(err);
        err.status === 401 && revalidator.revalidate();
        return null;
      }),
    );
    Promise.all(conceptQueries).then((result) => {
      const opts = result.map((data, idx) => {
        if (data) {
          const label =
            data?.name === data?.alternateName
              ? `${data?.name} ${data?.source.system}`
              : `${data?.name}(${data?.alternateName}) ${data?.source.system}`;
          return {
            value: data,
            label: label,
          } as ConceptSelectOptions;
        } else {
          return {
            value: {
              id: rankingConcepts[idx],
              name: rankingConcepts[idx],
            },
            label: rankingConcepts[idx],
          };
        }
      });
      setSelectedConceptOpts(opts);
    });
  }, [rankingConcepts]);
  return (
    <>
      <div className='eds-select'>
        <AsyncSelect
          className='eds-text-field--select tw-h-30 tw-w-[35rem]'
          classNamePrefix='eds-select'
          isMulti
          cacheOptions
          value={selectedConceptOpts}
          loadOptions={asyncOptions}
          onChange={handleSelectChange}
          ref={props.selectConceptsRef}
        />
        {props.validationError && (
          <p
            className='tw-text-red-900'
            style={{ color: 'var(--eds-color-text-danger)' }}
          >
            {props.validationError}
          </p>
        )}
      </div>
    </>
  );
}
